import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Package as PkgIcon, UserPlus } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Field } from "./Clients";
import { PackCoverageDialog } from "../components/PackCoverageDialog";
import { PageBand, OrangeButton } from "../components/layout/PageShell";

const fmtAud = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);

export default function Packages() {
  const { readOnly } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [clients, setClients] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", total_sessions: 10, price: 850 });
  const [assignForm, setAssignForm] = useState({ package_template_id: "", client_id: "" });
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { type: 'template'|'assigned', item }
  const [coverClientId, setCoverClientId] = useState(null);

  const load = async () => {
    const [{ data: tpl }, { data: ass }, { data: cl }] = await Promise.all([
      api.get("/packages/templates"),
      api.get("/packages/assigned"),
      api.get("/clients"),
    ]);
    setTemplates(tpl);
    setAssigned(ass);
    setClients(cl);
  };
  useEffect(() => { load(); }, []);

  const navigate = useNavigate();
  // Deep-link: `/packages?assign_client=<id>` (used by the Schedule pack-empty
  // warning) — open the assign dialog with that client preselected, once loaded.
  // Setting `assignReturnClientId` remembers we came from Schedule, so after a
  // successful assign we route back and pop a "safe to book" toast.
  const [params, setParams] = useSearchParams();
  const handledAssignParam = useRef(false);
  const [assignReturnClientId, setAssignReturnClientId] = useState(null);
  useEffect(() => {
    if (handledAssignParam.current) return;
    const cid = params.get("assign_client");
    if (!cid) return;
    if (!clients.length || !templates.length) return;
    handledAssignParam.current = true;
    setAssignForm({
      package_template_id: templates[0].package_template_id,
      client_id: cid,
    });
    setAssignReturnClientId(cid);
    setAssignOpen(true);
    params.delete("assign_client");
    setParams(params, { replace: true });
  }, [params, setParams, clients, templates]);

  const clientName = (id, snapshot) =>
    clients.find((c) => c.client_id === id)?.name || snapshot || "(client removed)";

  const saveTpl = async () => {
    if (!tplForm.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/packages/templates", { ...tplForm, total_sessions: Number(tplForm.total_sessions), price: Number(tplForm.price) });
      toast.success("Package created");
      setTplOpen(false);
      setTplForm({ name: "", total_sessions: 10, price: 850 });
      await load();
    } catch { toast.error("Could not create"); }
    finally { setSaving(false); }
  };

  const removeTpl = async (t) => {
    const prev = templates;
    setTemplates((p) => p.filter((x) => x.package_template_id !== t.package_template_id));
    try {
      await api.delete(`/packages/templates/${t.package_template_id}`);
      toast.success("Deleted");
    } catch {
      setTemplates(prev);
      toast.error("Could not delete");
    }
  };

  const openAssign = (tplId) => {
    setAssignForm({ package_template_id: tplId || templates[0]?.package_template_id || "", client_id: clients[0]?.client_id || "" });
    setAssignOpen(true);
  };

  const assign = async () => {
    if (!assignForm.package_template_id || !assignForm.client_id) { toast.error("Pick template and client"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/packages/assign", assignForm);
      toast.success(`Assigned · invoice ${data.invoice.invoice_number} created`);
      setAssignOpen(false);
      // If we came from Schedule via the pack-empty warning, bounce back so
      // the trainer can immediately book the session they were interrupted on.
      if (assignReturnClientId && assignReturnClientId === assignForm.client_id) {
        const cid = assignReturnClientId;
        setAssignReturnClientId(null);
        navigate(`/schedule?pack_sold=${cid}`);
        return;
      }
      await load();
      // Sold to a client with an active series? Offer to spend the new credits
      // on the upcoming weeks that aren't covered yet.
      try {
        const { data: cov } = await api.get(`/clients/${assignForm.client_id}/pack-coverage`);
        if (cov.eligible_total > 0 && cov.credits > 0) setCoverClientId(assignForm.client_id);
      } catch { /* coverage offer is optional */ }
    } catch { toast.error("Could not assign"); }
    finally { setSaving(false); }
  };

  const removeAssigned = async (p) => {
    const prev = assigned;
    setAssigned((a) => a.filter((x) => x.package_id !== p.package_id));
    try {
      await api.delete(`/packages/assigned/${p.package_id}`);
      toast.success("Removed");
    } catch {
      setAssigned(prev);
      toast.error("Could not delete");
    }
  };

  const confirmDelete = async () => {
    const pd = pendingDelete;
    setPendingDelete(null);
    if (!pd) return;
    if (pd.type === "template") await removeTpl(pd.item);
    else await removeAssigned(pd.item);
  };

  return (
    <div className="space-y-8" data-testid="packages-page">
      <PageBand
        testid="packages-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              Session Packs
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Packages
            </h1>
          </div>
        }
        right={
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
            <OrangeButton
              onClick={() => setTplOpen(true)}
              disabled={readOnly}
              title={readOnly ? "Your account is read only" : undefined}
              data-testid="add-package-button"
              className="order-1 sm:order-2 w-full sm:w-auto justify-center disabled:opacity-40"
              style={{ minHeight: 44 }}
            >
              <Plus className="w-4 h-4" /> New Template
            </OrangeButton>
            <button
              onClick={() => openAssign()}
              disabled={templates.length === 0 || clients.length === 0 || readOnly}
              title={readOnly ? "Your account is read only" : undefined}
              data-testid="assign-package-button"
              className="order-2 sm:order-1 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-full text-white text-sm font-semibold bg-white/15 backdrop-blur hover:bg-white/25 transition-colors disabled:opacity-40"
            >
              <UserPlus className="w-4 h-4 shrink-0" /> <span className="truncate">Assign to client</span>
            </button>
          </div>
        }
      />

      <section>
        <p className="tm-label mb-3">Templates</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {templates.length === 0 ? (
            <div className="col-span-full tm-card p-10 text-center text-[#6B635C]">
              <PkgIcon className="w-8 h-8 mx-auto mb-3 text-[#6B635C]" />
              No package templates yet. Create one above.
            </div>
          ) : templates.map((t) => (
            <div key={t.package_template_id} className="tm-card p-5" data-testid={`template-card-${t.package_template_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display text-xl font-bold uppercase">{t.name}</div>
                  <div className="text-xs text-[#6B635C] mt-1">{t.total_sessions} sessions · {fmtAud(t.price)}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setPendingDelete({ type: "template", item: t })} disabled={readOnly} title={readOnly ? "Your account is read only" : "Delete template"} data-testid={`template-delete-${t.package_template_id}`} className="h-8 w-8 text-[#6B635C] hover:text-[#A8372A] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-[#6B635C] mb-3">
                {fmtAud(t.price / t.total_sessions)} per session
              </div>
              <Button onClick={() => openAssign(t.package_template_id)} disabled={readOnly} title={readOnly ? "Your account is read only" : undefined} size="sm" variant="outline" data-testid={`template-assign-${t.package_template_id}`} className="w-full border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2] hover:text-[#0F6B6B] hover:border-[#0F6B6B] disabled:opacity-40">
                <UserPlus className="w-3.5 h-3.5" /> Assign to client
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="tm-label mb-3">Assigned packs</p>
        <div className="tm-card overflow-hidden" data-testid="assigned-list">
          {assigned.length === 0 ? (
            <div className="p-10 text-center text-[#6B635C]">No assigned packs yet.</div>
          ) : assigned.map((p) => {
            const pct = Math.max(0, Math.min(100, (p.sessions_remaining / p.total_sessions) * 100));
            return (
              <div key={p.package_id} className="px-5 py-4 border-b last:border-b-0 flex items-center gap-4" style={{ borderColor: "#E8E1D6" }} data-testid={`assigned-row-${p.package_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold">{clientName(p.client_id, p.client_name)}</span>
                    <span className="text-xs text-[#6B635C] uppercase tracking-widest">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 h-2 bg-[#E8E1D6] rounded-full overflow-hidden max-w-xs">
                      <div className="h-full bg-[#0F6B6B]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-[#2A2623] whitespace-nowrap" data-testid={`assigned-remaining-${p.package_id}`}>
                      <b className="text-[#0F6B6B]">{p.sessions_remaining}</b> / {p.total_sessions} left
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold">{fmtAud(p.price)}</div>
                  <div className="text-xs text-[#6B635C]">paid up-front</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setPendingDelete({ type: "assigned", item: p })} disabled={readOnly} title={readOnly ? "Your account is read only" : "Remove pack"} data-testid={`assigned-delete-${p.package_id}`} className="h-8 w-8 text-[#6B635C] hover:text-[#A8372A] disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#6B635C] mt-3">
          When you book a new session for a client with an active pack, one session is auto-deducted from the remaining balance.
        </p>
      </section>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New package template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name *">
              <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} placeholder="10-Session Pack" data-testid="tpl-name" className="bg-[#FAF7F2] border-[#E8E1D6]" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Total sessions">
                <Input type="number" min="1" value={tplForm.total_sessions} onChange={(e) => setTplForm({ ...tplForm, total_sessions: e.target.value })} data-testid="tpl-sessions" className="bg-[#FAF7F2] border-[#E8E1D6]" />
              </Field>
              <Field label="Price (AUD, inc GST)">
                <Input type="number" min="0" step="0.01" value={tplForm.price} onChange={(e) => setTplForm({ ...tplForm, price: e.target.value })} data-testid="tpl-price" className="bg-[#FAF7F2] border-[#E8E1D6]" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)} data-testid="tpl-cancel" className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]">Cancel</Button>
            <Button onClick={saveTpl} disabled={saving} data-testid="tpl-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">
              {saving ? "Saving..." : "Create template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Assign package</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Package *">
              <Select value={assignForm.package_template_id} onValueChange={(v) => setAssignForm({ ...assignForm, package_template_id: v })}>
                <SelectTrigger data-testid="assign-template" className="bg-[#FAF7F2] border-[#E8E1D6]"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                  {templates.map((t) => <SelectItem key={t.package_template_id} value={t.package_template_id}>{t.name} · {fmtAud(t.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Client *">
              <Select value={assignForm.client_id} onValueChange={(v) => setAssignForm({ ...assignForm, client_id: v })}>
                <SelectTrigger data-testid="assign-client" className="bg-[#FAF7F2] border-[#E8E1D6]"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <p className="text-xs text-[#6B635C]">An invoice will be auto-generated for the package price (inc GST).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="assign-cancel" className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2]">Cancel</Button>
            <Button onClick={assign} disabled={saving} data-testid="assign-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold">
              {saving ? "Assigning..." : "Assign & invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="package-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">
              {pendingDelete?.type === "template" ? "Delete template?" : "Remove assigned pack?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              {pendingDelete?.type === "template"
                ? `Delete "${pendingDelete?.item?.name}" template? Existing assigned packs stay intact.`
                : `Remove "${pendingDelete?.item?.name}" for ${pendingDelete ? clientName(pendingDelete.item.client_id, pendingDelete.item.client_name) : ""}? This won't delete past invoices.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="package-delete-cancel"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="package-delete-confirm"
              className="bg-[#A8372A] hover:bg-[#8E2E23] text-[#FFFFFF]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {coverClientId && (
        <PackCoverageDialog
          open={!!coverClientId}
          onOpenChange={(o) => !o && setCoverClientId(null)}
          clientId={coverClientId}
          onDone={load}
        />
      )}
    </div>
  );
}
